import { supabase } from '@/integrations/supabase/client';

export interface RawProfilePostRow {
  id: string;
  content: string;
  created_at: string;
  user_id: string;
  visibility: string;
  profiles: {
    full_name: string | null;
    belt_rank: string | null;
    profile_picture_url: string | null;
    username: string | null;
  } | null;
}

/**
 * Fields a caller may set on a profile.
 *
 * Deliberately open-ended: `update_profile` assigns every column of `profiles`
 * through COALESCE, so a payload naming one field leaves the rest alone, and
 * callers across the app send very different subsets.
 */
export type ProfileUpdateFields = Record<string, string | number | boolean | null>;

export const profileRepository = {
  /**
   * Writes the named fields to a profile, leaving every other column untouched.
   *
   * The single place in the app that calls the `update_profile` RPC. It exists
   * because the wallet feature needs to set `profiles.country` — the field
   * payout setup gates on — and a payout screen reaching for its own RPC call
   * gave that one function two independent client callers. The write belongs to
   * the feature that owns the table.
   *
   * COALESCE is what makes a partial payload safe: absent and NULL keys keep
   * the current value, so setting one field cannot blank a name, belt, academy,
   * socials, state or city. `payoutCountryPersistence.test.tsx` asserts that
   * against the shipped migration rather than trusting it.
   *
   * Rejects when the write fails. A caller that cannot tell a failed write from
   * a successful one would report a save that never happened.
   */
  async updateProfileFields(profileId: string, fields: ProfileUpdateFields): Promise<void> {
    const { error } = await supabase.rpc('update_profile', {
      profile_id:   profileId,
      profile_data: fields,
    });

    if (error) throw error;
  },

  /** The profile's display name, or null when the profile has none or no row exists. */
  async getFullName(profileId: string): Promise<string | null> {
    const { data, error } = await supabase
      .from('profiles')
      .select('full_name')
      .eq('id', profileId)
      .maybeSingle();
    if (error) throw error;
    return data?.full_name ?? null;
  },

  async getPostsByUser(userId: string, limit = 10): Promise<RawProfilePostRow[]> {
    const { data, error } = await supabase
      .from('posts')
      .select('*, profiles:profiles!inner(full_name, belt_rank, profile_picture_url, username)')
      .eq('user_id', userId)
      .order('created_at', { ascending: false })
      .limit(limit);
    if (error) throw error;
    return (data ?? []) as unknown as RawProfilePostRow[];
  },
};
